import { getCookie } from "cookies-next";
import dateFormat from "date-fns/format";
import intervalToDuration from "date-fns/intervalToDuration";
import isValid from "date-fns/isValid";
import { enGB, ru, et, lt, lv, pl } from "date-fns/locale";
import parseISO from "date-fns/parseISO";
import cloneDeep from "lodash/cloneDeep";

import { getPageFolderName, getPageTypes } from "@config/cms";
import {
  sortTicketOptions,
  FILE_TYPES,
  isThreeDExtension,
  isVideoExtension,
  isImageExtension,
} from "@config/config";

const locales = { en: enGB, ru, et, lt, lv, pl };

// by providing a default string of 'PP' or any of its variants for `formatStr`
// it will format dates in whichever way is appropriate to the locale
// export const localizedDate = (date, formatStr = "PP") => {
//   const isValidDate = /^\d{2,4}-\d{2,4}-\d{2,4}$/.test(date);

//   if (isValidDate) {
//     return "Invalid Date";
//   }

//   return dateFnsFormat(new Date(date), formatStr, {
//     locale: locales[locale],
//   });
// };

export const getSearchQuery = (search, currencyName) => {
  const {
    promocode,
    departDate,
    returnDate,
    from,
    to,
    passengers,
    passengersCount,
  } = search;
  const affiliateId = getCookie("affiliateId");
  return {
    promocode,
    departDate: dateFormat(new Date(departDate), "yyyy-MM-dd"),
    ...(returnDate
      ? {
          returnDate: dateFormat(new Date(returnDate), "yyyy-MM-dd"),
        }
      : {}),
    currency: currencyName,
    fromBusStopId: from.BusStopId,
    toBusStopId: to.BusStopId,
    adult: passengers.adult,
    senior: passengers.senior,
    youth: passengers.youth,
    pupil: passengers.pupil,
    child: passengers.child,
    affiliateId,
  };
};

export const getCorrectDate = (date) => {
  try {
    if (!isValid(new Date(date))) {
      throw new Error(`Date: ${date} is not valid`);
    }

    let updateDate = "";
    if (typeof date === "string") {
      const [left, right] = date.split("T");
      updateDate = right
        ? new Date(left.replaceAll("-", "/")) // TODO: write correct case
        : new Date(left.replaceAll("-", "/"));
    } else {
      updateDate = new Date(date);
    }

    return updateDate;
  } catch (e) {
    console.error("utils getCorrectDate", e);
  }
};

export const localizedDate = (date, format = "PP", locale) => {
  // if (!isValid(new Date(date))) return null;

  const isValidDate =
    typeof date === "string"
      ? !isValid(parseISO(date))
      : !isValid(new Date(date));

  if (isValidDate) return null;

  return dateFormat(new Date(date), format, {
    locale: locales[locale],
  });
};

export const dateFormatForExponea = (
  date,
  formatStr = "yyyy-MM-dd'T'HH:mm:ssxxx",
) => {
  const newDate = new Date(date);

  if (!isValid(new Date(newDate))) return "Invalid Date";

  return dateFormat(newDate, formatStr);
};

export const extractContent = (s, space) => {
  const span = document.createElement("span");
  span.innerHTML = s;
  if (space) {
    const children = span.querySelectorAll("*");
    for (let i = 0; i < children.length; i++) {
      if (children[i].textContent) {
        children[i].textContent += " ";
      } else {
        children[i].innerText += " ";
      }
    }
  }
  return [span.textContent || span.innerText].toString().replace(/ +/g, " ");
};

export const addBlankAttrToHtml = (text = "") =>
  text.replace(/href/g, 'target="_black" href');

export const organizeStopsNew = (legs, showTime) => {
  const stops = [];
  const legsCount = legs.length;

  if (!legsCount) return stops;

  [...legs].sort((a, b) => a.OrderNumber - b.OrderNumber);

  legs.forEach((leg, key) => {
    stops.push({
      from: leg.DepartureStopName,
      to: leg.DestinationStopName,
      stopName: leg.DestinationStopName,
      country: leg.DepartureStopLocation.CountryName,
      city: leg.DepartureStopLocation.CityName,
      fromTime: showTime(leg.DepartureDatetime),
      toTime: showTime(leg.ArrivalDatetime),
      transfer: key > 0,
      busEquipment: leg?.BusEquipment || [],
      brandName: leg?.BrandName,
      busCompanyName: leg?.BusCompanyName,
      isPartner: !leg?.BrandName?.startsWith("Lux"),
      intermediateStops: leg.IntermediateStops.map((stop) => {
        return {
          stopName: stop.StopName,
          time: showTime(stop.DepartureDateTime),
        };
      }),
      interval:
        key === 0
          ? null
          : intervalToDuration({
              start: new Date(legs[key - 1].ArrivalDatetime),
              end: new Date(leg.DepartureDatetime),
            }),
    });
  });

  return stops;
};

export const updatePrices = (prices, products) => {
  const _products = cloneDeep(products);

  prices.forEach((item) => {
    item.OutboundLegs.forEach((leg) => {
      _products.OutboundJourneys[0].Legs[leg.OrderNumber - 1].SeatInfo =
        _products.OutboundJourneys[0].Legs[leg.OrderNumber - 1].SeatInfo.map(
          (info) => {
            const prices = leg.Prices.filter(
              (price) => info.IsBusinessClass === price.IsBusinessClass,
            );

            const hasCampaign = prices.some((price) => price.IsCampaign);

            const IsCampaign = hasCampaign
              ? (info.IsCampaign ?? hasCampaign)
              : null;

            return item.ProductId === info.ProductId
              ? {
                  ...info,
                  HasCampaign: hasCampaign,
                  IsCampaign,
                  Prices: leg.Prices,
                }
              : info;
          },
        );
    });

    if (item.InboundLegs.length) {
      item.InboundLegs.forEach((leg) => {
        _products.InboundJourneys[0].Legs[leg.OrderNumber - 1].SeatInfo =
          _products.InboundJourneys[0].Legs[leg.OrderNumber - 1].SeatInfo.map(
            (info) => {
              const prices = leg.Prices.filter(
                (price) => info.IsBusinessClass === price.IsBusinessClass,
              );

              const hasCampaign = prices.some((price) => price.IsCampaign);

              return item.ProductId === info.ProductId
                ? {
                    ...info,
                    HasCampaign: hasCampaign,
                    IsCampaign: hasCampaign
                      ? (info.IsCampaign ?? hasCampaign)
                      : null,
                    Prices: leg.Prices,
                  }
                : info;
            },
          );
      });
    }
  });

  return _products;
};

export const updatePassengersPrices = (passengers, products) => {
  const pass = products.OutboundJourneys[0].Products;

  const passengersPrices = pass.map((p) => {
    return passengers.filter((item) => item.ProductId === p.ProductId)[0]
      .TotalPrice;
  });

  return passengersPrices;
};

export const updatePassengersPricesV2 = (data, products) => {
  // Find all extra seat ProductIds from local store (products)
  // Backend doesn't return IsExtraSeat field, only local store has it
  const extraSeatProductIds = [];
  if (products.OutboundJourneys[0]?.Legs) {
    products.OutboundJourneys[0].Legs.forEach((leg) => {
      leg.SeatInfo.forEach((seat) => {
        if (seat.IsExtraSeat) {
          extraSeatProductIds.push(seat.ProductId);
        }
      });
    });
  }
  if (products.InboundJourneys[0]?.Legs) {
    products.InboundJourneys[0].Legs.forEach((leg) => {
      leg.SeatInfo.forEach((seat) => {
        if (seat.IsExtraSeat) {
          extraSeatProductIds.push(seat.ProductId);
        }
      });
    });
  }

  // Get all passengers from products (not from data!)
  const allPassengers = products.OutboundJourneys[0]?.Products || [];

  // Filter out extra seats from the main passenger list
  const passengers = allPassengers.filter(
    (p) => !extraSeatProductIds.includes(p.ProductId),
  );

  let passengerPrices = {
    totalServiceFee: 0,
    prices: [],
  };

  if (data.OutboundJourneys[0]?.Products) {
    data.OutboundJourneys[0].Products.forEach((p) => {
      const salesFeePrice = p.SalesFee?.Price ?? 0;
      passengerPrices.totalServiceFee += salesFeePrice;
    });
  }

  if (data.InboundJourneys[0]?.Products?.length) {
    data.InboundJourneys[0].Products.forEach((product) => {
      const salesFeePrice = product.SalesFee?.Price ?? 0;
      passengerPrices.totalServiceFee += salesFeePrice;
    });
  }

  passengerPrices.prices = passengers.map((passenger) => {
    const outboundProduct = data.OutboundJourneys[0].Products.find(
      (p) => p.ProductId === passenger.ProductId,
    );
    const inboundProduct = data.InboundJourneys[0]?.Products?.find(
      (p) => p.ProductId === passenger.ProductId,
    );

    let price = 0;
    let seatFee = 0;
    const extraSeats = [];

    if (outboundProduct) {
      const outboundSalesFee = outboundProduct.SalesFee?.Price ?? 0;
      price += outboundProduct.TotalPrice - outboundSalesFee;
    }

    if (inboundProduct) {
      const inboundSalesFee = inboundProduct.SalesFee?.Price ?? 0;
      price += inboundProduct.TotalPrice - inboundSalesFee;
    }

    // Find seatFee in data (from backend)
    if (data.OutboundJourneys[0]?.Legs) {
      data.OutboundJourneys[0].Legs.forEach((leg) => {
        const seat = leg.SeatInfo.find(
          (s) => s.ProductId === passenger.ProductId,
        );
        if (seat?.SeatSelectionFee) {
          seatFee += seat.SeatSelectionFee.Amount;
        }
      });
    }

    // Find extra seats in products (local store, which has IsExtraSeat field)
    if (products.OutboundJourneys[0]?.Legs) {
      products.OutboundJourneys[0].Legs.forEach((leg) => {
        const extraSeat = leg.SeatInfo.find(
          (s) => s.IsExtraSeat && s.ownerProductId === passenger.ProductId,
        );
        if (extraSeat) {
          const extraProduct = data.OutboundJourneys[0].Products.find(
            (p) => p.ProductId === extraSeat.ProductId,
          );
          if (extraProduct) {
            const extraSalesFee = extraProduct.SalesFee?.Price ?? 0;
            const extraPrice = extraProduct.TotalPrice - extraSalesFee;
            const fareClass =
              extraSeat.RequestedFareClass || extraSeat.fareClass?.FareClass;
            const isPet = fareClass === "BONUS_SCHEME_GROUP.PET";

            extraSeats.push({
              id: extraSeat.ProductId,
              price: extraPrice,
              seatNumber: extraSeat.SeatNumber,
              type: isPet ? "pet" : "extra",
            });
          }
        }
      });
    }

    // Find seatFee in data (from backend) for inbound
    if (data.InboundJourneys[0]?.Legs) {
      data.InboundJourneys[0].Legs.forEach((leg) => {
        const seat = leg.SeatInfo.find(
          (s) => s.ProductId === passenger.ProductId,
        );
        if (seat?.SeatSelectionFee) {
          seatFee += seat.SeatSelectionFee.Amount;
        }
      });
    }

    // Find extra seats in products (local store) for inbound
    if (products.InboundJourneys[0]?.Legs) {
      products.InboundJourneys[0].Legs.forEach((leg) => {
        const extraSeat = leg.SeatInfo.find(
          (s) => s.IsExtraSeat && s.ownerProductId === passenger.ProductId,
        );
        if (extraSeat) {
          const extraProduct = data.InboundJourneys[0].Products.find(
            (p) => p.ProductId === extraSeat.ProductId,
          );
          if (extraProduct) {
            const extraSalesFee = extraProduct.SalesFee?.Price ?? 0;
            const extraPrice = extraProduct.TotalPrice - extraSalesFee;
            const fareClass =
              extraSeat.RequestedFareClass || extraSeat.fareClass?.FareClass;
            const isPet = fareClass === "BONUS_SCHEME_GROUP.PET";

            extraSeats.push({
              id: extraSeat.ProductId,
              price: extraPrice,
              seatNumber: extraSeat.SeatNumber,
              type: isPet ? "pet" : "extra",
            });
          }
        }
      });
    }

    return {
      id: passenger.ProductId,
      price,
      seatFee,
      extraSeats,
    };
  });

  return { passengerPrices };
};

export const allowedLetters =
  "a-zA-ZаАбБвВгГдДеЕёЁжЖзЗиИйЙкКлЛмМнНоОпПрРсСтТуУфФхХцЦчЧшШщЩъЪыЫьЬэЭюЮяЯàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ðĀĒĢĶĻŅāēģķļņ";
export const regExpForNames =
  /^[a-zA-ZаАбБвВгГдДеЕёЁжЖзЗиИйЙкКлЛмМнНоОпПрРсСтТуУфФхХцЦчЧшШщЩъЪыЫьЬэЭюЮяЯàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ðĀĒĢĶĻŅāēģķļņ ,.'-]+$/;

export const regExpForCorporateAccountName = /^[a-zA-Z0-9_.\-]+$/;

// export const regExpForEmail =
//   /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const regExpForEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,}$/;

export const createThanksSuccessAnaliticsData = ({
  returnDate,
  departDate,
  from,
  to,
  currencyName,
  totalPrice,
  promocode,
  objects,
}) => {
  try {
    let Id = null;
    const products = [];

    objects.forEach((object) => {
      object.Passengers.forEach((pass) => {
        pass.Tickets.forEach((ticket, key) => {
          products.push({
            id: `${object.DepartureCityName}-${object.DestinationCityName}`,
            name: `${object.DepartureCityName}-${object.DestinationCityName}`,
            price: ticket.Price,
            category: `${from.CountryName}-${to.CountryName}`,
            quantity: 1,
            variant: "one-way",
            brand: `${object.Legs[key].BrandName}`,
          });
        });
      });
    });

    Id = objects?.[0]?.ShoppingBasketCode;

    return {
      tripType: returnDate ? "return-trip" : "one-way",
      departureStopId: from.BusStopId,
      destinationStopId: to.BusStopId,
      currencyCode: currencyName,
      route: `${from.CityName}-${to.CityName}`,
      price: totalPrice,
      promocode: promocode || null,
      departureDestination: `${from.CountryName}-${to.CountryName}`,
      stage: "purchase",
      event: "payment-success",
      quantity: products.length,
      Id,
      products,
      departDate: dateFormat(new Date(departDate), "yyyy-MM-dd"),
      ...(returnDate
        ? {
            returnDate: dateFormat(new Date(returnDate), "yyyy-MM-dd"),
          }
        : {}),
    };
  } catch (e) {
    console.error("Error on createThanksSuccessAnaliticsData", e);
  }
};

export const round = (num) => +`${Math.round(`${num}e+2`)}e-2`;

export const firstLetterToLoverCase = (str) => {
  return str && typeof str === "string"
    ? str.charAt(0).toLowerCase() + str.slice(1)
    : "";
};

export const firstLetterToUpperCase = (str) => {
  return str && typeof str === "string"
    ? str.charAt(0).toUpperCase() + str.slice(1)
    : "";
};

export const getSlugAndType = (url) => {
  let parts = url.split("?");
  parts = parts[0].split("/").filter((i) => !!i);

  const result = {
    type: null,
    slug: null,
  };

  switch (parts.length) {
    case 0:
      result.slug = "/";
      result.type = getPageTypes().regular;
      break;
    case 1:
      result.slug = parts[0];
      result.type = getPageTypes().regular;
      break;
    case 2:
      result.slug = parts[1];

      const isTypeValid = getPageFolderName(true).includes(
        parts[0].toLowerCase(),
      );

      Object.entries(getPageFolderName()).forEach(([key, value]) => {
        if (value === parts[0].toLowerCase() && isTypeValid) {
          result.type = key;
        }
      });

      break;
    case 3:
      result.slug = parts[2];
      {
        const folderCandidate = parts[1].toLowerCase();
        const isFolderSegment =
          getPageFolderName(true).includes(folderCandidate);
        if (isFolderSegment) {
          Object.entries(getPageFolderName()).forEach(([key, value]) => {
            if (value === folderCandidate) {
              result.type = key;
            }
          });
        }
      }
      break;

    default:
      break;
  }

  return result;
};

export const checkIsTicketsFlowPage = (route) =>
  /(change-)?tickets/g.test(route);

export const getPageImage = (pageData) => {
  const image =
    pageData.seo.thumbnailBig ||
    pageData.seo.thumbnail ||
    pageData.seo.thumbnail;
  return image;
};

export const getAvailableLuxSeat = (products, isOutbound, activeBus) => {
  const bus =
    products[isOutbound ? "OutboundJourneys" : "InboundJourneys"][0].Legs[
      activeBus - 1
    ].SeatPlan.Buses[0];

  const floors = bus.Floors || [];

  // check all floors of the bus
  for (const floor of floors) {
    const seatRows = floor?.SeatRows || [];

    for (const seatRow of seatRows) {
      const luxSeat = seatRow.Seats.find(
        (seat) => seat.IsAvailable && seat.SeatClass === "SEAT_CLASS.A",
      );
      if (luxSeat) {
        return luxSeat;
      }
    }
  }

  return false;
};

export const getLastPartOfUrl = (url) => {
  const match = url.match(/\/([^\/]+)\/?$/);
  return match ? match[1] : null;
};

export const compareByTime = (a, b) => {
  const departureA = new Date(a.DepartureDateTime).getTime();
  const departureB = new Date(b.DepartureDateTime).getTime();

  const arrivalA = new Date(a.ArrivalDateTime).getTime();
  const arrivalB = new Date(b.ArrivalDateTime).getTime();

  const departureDiff = departureA - departureB;

  return departureDiff !== 0 ? departureDiff : arrivalA - arrivalB;
};

export const getMinPrice = (ticket) => {
  const prices = ticket.PriceClasses.map((i) => i.Price);
  return Math.min(...prices);
};

const compareByDuration = (a, b) => {
  const durationA =
    new Date(a.ArrivalDateTime).getTime() -
    new Date(a.DepartureDateTime).getTime();
  const durationB =
    new Date(b.ArrivalDateTime).getTime() -
    new Date(b.DepartureDateTime).getTime();

  return durationA - durationB;
};

export const sortTickets = (tickets, setLists, sortType) => {
  if (sortType === sortTicketOptions.transfers.id) {
    const directTickets = tickets.filter((ticket) => ticket.Legs.length === 1);
    const transfersTickets = tickets.filter(
      (ticket) => ticket.Legs.length >= 2,
    );

    // Sort direct flights by time (by departure and arrival time)
    const sortedDirectTickets = directTickets.sort(compareByTime);

    // Sorting out connecting tickets:
    // First by the number of “legs”, then within each group by departure and arrival time
    const sortedTransfersTickets = transfersTickets.sort((a, b) => {
      const legsDiff = a.Legs.length - b.Legs.length; // Sort by number of legs
      return legsDiff !== 0 ? legsDiff : compareByTime(a, b); // If the legs are the same, sort by departure and arrival time
    });

    const lists = [];
    if (sortedDirectTickets.length > 0) {
      lists.push({ tickets: sortedDirectTickets });
    }
    if (sortedTransfersTickets.length > 0) {
      lists.push({ tickets: sortedTransfersTickets });
    }

    setLists(lists);
  } else if (sortType === sortTicketOptions.time.id) {
    // setLists([{ tickets: [...tickets].sort(compareByTime) }]);
    setLists([{ tickets: [...tickets].sort(compareByDuration) }]);
  } else if (sortType === sortTicketOptions.price.id) {
    setLists([
      {
        tickets: [...tickets].sort((a, b) => {
          const minPriceA = getMinPrice(a);
          const minPriceB = getMinPrice(b);
          return minPriceA - minPriceB;
        }),
      },
    ]);
  } else {
    throw new Error("Invalid sort type");
  }
};

export const getFileExtension = (url) => {
  const match = url.match(/\.([^\.]+)$/);
  return match ? match[1] : null;
};

export const getFileType = (src) => {
  if (!src) return null;

  // const extension = src.split(".").pop()?.toLowerCase();
  const extension = getFileExtension(src);

  // 3D model extensions
  if (isThreeDExtension(extension)) {
    return FILE_TYPES.threeD;
  }

  // Video extensions
  if (isVideoExtension(extension)) {
    return FILE_TYPES.video;
  }

  // Image extensions
  if (isImageExtension(extension)) {
    return FILE_TYPES.image;
  }

  return null;
};

export const isLocalhost = () => {
  return (
    typeof window !== "undefined" && window.location.hostname === "localhost"
  );
};
